import { useTranslation } from 'react-i18next';
import { InkedTitle } from '../components/InkedTitle';
import { studio } from '../data';
import { useLang } from '../hooks/useLang';

export function PrivacyPage() {
  const { t } = useTranslation();
  const lang = useLang();

  const lastUpdate = new Date('2025-10-12').toLocaleDateString(lang === 'it' ? 'it-IT' : 'en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const body = lang === 'it' ? itBody : enBody;

  return (
    <section className="px-6 pt-36 pb-24 md:px-10 md:pt-48 md:pb-36">
      <div className="mx-auto max-w-[1800px]">
      <p className="text-sm font-display lowercase tracking-tight text-ink-muted">
        {t('footer.legal.privacy')} · {lastUpdate}
      </p>
      <InkedTitle
        as="h1"
        immediate
        delay={0.15}
        className="mt-6 font-display tracking-display leading-[0.9] text-5xl md:text-7xl lowercase max-w-4xl"
      >
        {body.title}
      </InkedTitle>

      <div className="mt-16 grid gap-12 md:grid-cols-12">
        <aside className="md:col-span-3 grid content-start gap-4">
          <p className="text-sm font-display lowercase tracking-tight text-ink-muted">
            {lang === 'it' ? 'Titolare' : 'Controller'}
          </p>
          <p>
            {studio.legalName}
            <br />
            {studio.address.street}
            <br />
            {studio.address.zip} {studio.address.city}
          </p>
          <a href={`mailto:${studio.email}`} className="hover:text-ink-paper transition-colors">
            {studio.email}
          </a>
        </aside>
        <div className="md:col-span-8 md:col-start-5 grid gap-10 text-base md:text-lg text-ink-paper/85 leading-relaxed">
          {body.sections.map((s) => (
            <section key={s.h} className="grid gap-3">
              <h2 className="font-display tracking-display text-2xl md:text-3xl lowercase">
                {s.h}
              </h2>
              {s.p.map((p) => (
                <p key={p}>{p}</p>
              ))}
            </section>
          ))}
        </div>
      </div>
      </div>
    </section>
  );
}

const itBody = {
  title: 'Trattamento dei dati personali.',
  sections: [
    {
      h: 'Cosa raccogliamo',
      p: [
        'Quando compili il form di prenotazione ti chiediamo nome, email, telefono (facoltativo), preferenze sull’artista e sullo stile, l’idea che hai in mente, una finestra temporale e un budget orientativo. Quando ti iscrivi alla newsletter raccogliamo solo la tua email.',
        'Non chiediamo dati sensibili. Non profiliamo. Non rivendiamo nulla.',
      ],
    },
    {
      h: 'Perché lo facciamo',
      p: [
        'I dati del form di prenotazione servono esclusivamente per rispondere alla richiesta, fissare la consulenza e — se si procede — eseguire la prestazione (base giuridica: esecuzione di misure precontrattuali e del contratto, art. 6.1.b GDPR). I dati della newsletter sono trattati sulla base del tuo consenso (art. 6.1.a GDPR) e puoi revocarlo in qualsiasi momento dal link in fondo a ogni email.',
      ],
    },
    {
      h: 'Per quanto li conserviamo',
      p: [
        'I dati di prenotazione restano nei nostri archivi per 24 mesi dall’ultima interazione. I dati fiscali relativi a sedute effettivamente eseguite seguono gli obblighi di legge (10 anni). L’email della newsletter resta finché non disiscrivi.',
      ],
    },
    {
      h: 'I tuoi diritti',
      p: [
        'Puoi sempre chiederci di vedere i dati che abbiamo su di te, di correggerli, di limitarne il trattamento, di portarli altrove o di cancellarli. Basta scrivere a studio@inkhaus.it. Puoi anche presentare un reclamo al Garante per la protezione dei dati personali.',
      ],
    },
    {
      h: 'Cookie',
      p: [
        'Il sito non usa cookie di profilazione. Usa il localStorage del browser per ricordare la lingua scelta e per memorizzare temporaneamente le richieste di prenotazione che invii (così non vanno perse se la pagina si chiude prima dell’invio). Niente terze parti.',
      ],
    },
  ],
};

const enBody = {
  title: 'Processing of personal data.',
  sections: [
    {
      h: 'What we collect',
      p: [
        'When you fill the booking form we ask for name, email, phone (optional), artist and style preferences, the idea you have in mind, a timeframe and a rough budget. When you subscribe to the newsletter we only collect your email.',
        'We don’t ask for sensitive data. We don’t profile. We don’t resell anything.',
      ],
    },
    {
      h: 'Why we do it',
      p: [
        'Booking form data is used solely to reply, set the consultation and — if we proceed — perform the service (legal basis: pre-contractual and contractual measures, art. 6.1.b GDPR). Newsletter data is processed on the basis of your consent (art. 6.1.a GDPR), revocable at any time from the link at the bottom of every email.',
      ],
    },
    {
      h: 'How long we keep it',
      p: [
        'Booking data stays in our archive for 24 months from last interaction. Fiscal data linked to sessions actually performed follows legal retention (10 years). Newsletter email stays until you unsubscribe.',
      ],
    },
    {
      h: 'Your rights',
      p: [
        'You can always ask to view the data we hold on you, to correct it, restrict its processing, port it elsewhere or delete it. Just write to studio@inkhaus.it. You can also file a complaint with the Italian Data Protection Authority.',
      ],
    },
    {
      h: 'Cookies',
      p: [
        'The site uses no profiling cookies. It uses browser localStorage to remember your language and to temporarily save the booking requests you send (so they aren’t lost if the page closes before submit). No third parties.',
      ],
    },
  ],
};
